import React, { useState } from 'react';
import type { ServiceStatus } from '../services/patientsService';

interface ServiceStatusIconsProps {
  status: ServiceStatus | null | undefined;
  size?: number;
  gap?: number;
}

type IconKey = 'agent' | 'push' | 'sleep';

interface IconItem {
  key: IconKey;
  color: string;
  background: string;
  border: string;
  label: string;
  description: string;
}

const AgentSvg: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="7" width="16" height="12" rx="2" />
    <path d="M12 7V3" />
    <circle cx="12" cy="3" r="1" />
    <circle cx="9" cy="13" r="1" />
    <circle cx="15" cy="13" r="1" />
    <path d="M9 17h6" />
  </svg>
);

const BellSvg: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

const MoonSvg: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const ICON_RENDER: Record<IconKey, React.FC<{ size: number; color: string }>> = {
  agent: AgentSvg,
  push: BellSvg,
  sleep: MoonSvg,
};

const buildIcons = (status: ServiceStatus): IconItem[] => {
  const agent: IconItem = (() => {
    if (!status.agentEnabled) {
      return {
        key: 'agent',
        color: '#9ca3af',
        background: '#f3f4f6',
        border: '#e5e7eb',
        label: 'Agente deshabilitado',
        description: 'El agente no está habilitado para este paciente.',
      };
    }
    if (!status.agentProactiveEnabled) {
      return {
        key: 'agent',
        color: '#b45309',
        background: '#fef3c7',
        border: '#fcd34d',
        label: 'Agente habilitado, proactivo deshabilitado',
        description: 'El chat puede ejecutar acciones, pero el sistema no enviará mensajes proactivos.',
      };
    }
    return {
      key: 'agent',
      color: '#047857',
      background: '#d1fae5',
      border: '#6ee7b7',
      label: 'Agente habilitado y proactivo',
      description: 'El chat ejecuta acciones y el sistema puede enviar mensajes proactivos al paciente.',
    };
  })();

  const push: IconItem = status.pushNotifications
    ? {
        key: 'push',
        color: '#047857',
        background: '#d1fae5',
        border: '#6ee7b7',
        label: 'Notificaciones push activas',
        description: 'El paciente puede recibir notificaciones push en su dispositivo.',
      }
    : {
        key: 'push',
        color: '#9ca3af',
        background: '#f3f4f6',
        border: '#e5e7eb',
        label: 'Notificaciones push inactivas',
        description: 'El paciente no tiene notificaciones push activadas.',
      };

  const sleep: IconItem = status.sleepEnabled
    ? {
        key: 'sleep',
        color: '#047857',
        background: '#d1fae5',
        border: '#6ee7b7',
        label: 'Seguimiento de sueño activo',
        description: 'El seguimiento de sueño está habilitado para este paciente.',
      }
    : {
        key: 'sleep',
        color: '#9ca3af',
        background: '#f3f4f6',
        border: '#e5e7eb',
        label: 'Seguimiento de sueño inactivo',
        description: 'El seguimiento de sueño no está habilitado.',
      };

  return [agent, push, sleep];
};

const Icon: React.FC<{ item: IconItem; size: number }> = ({ item, size }) => {
  const [hover, setHover] = useState(false);
  const Svg = ICON_RENDER[item.key];
  const inner = Math.round(size * 0.62);
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <span
        aria-label={item.label}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size,
          height: size,
          borderRadius: '50%',
          background: item.background,
          border: `1px solid ${item.border}`,
          color: item.color,
        }}
      >
        <Svg size={inner} color={item.color} />
      </span>
      {hover && (
        <span
          role="tooltip"
          style={{
            position: 'absolute',
            bottom: `calc(100% + 8px)`,
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#1f2937',
            color: '#fff',
            fontSize: 12,
            lineHeight: 1.4,
            padding: '8px 10px',
            borderRadius: 6,
            whiteSpace: 'normal',
            width: 220,
            boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
            zIndex: 50,
            pointerEvents: 'none',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 2 }}>{item.label}</div>
          <div style={{ opacity: 0.85 }}>{item.description}</div>
        </span>
      )}
    </span>
  );
};

const ServiceStatusIcons: React.FC<ServiceStatusIconsProps> = ({ status, size = 28, gap = 8 }) => {
  if (!status) {
    return <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>;
  }
  const icons = buildIcons(status);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap }}>
      {icons.map(item => (
        <Icon key={item.key} item={item} size={size} />
      ))}
    </span>
  );
};

export default ServiceStatusIcons;
